import { useKeycloak } from "@react-keycloak/web";
import { FunctionComponent, useEffect } from "react";
import { connect } from "react-redux";
import { useRoutes } from "react-router-dom";
import { codeHttpClient } from "core/http/clients/code/code.http";
import { authAsync } from "core/store/modules/auth";
import { Dispatch, RootState } from "core/store/store.types";
import routes from "routes";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import useTheme from "core/hooks/useTheme";
import createTheme from "core/theme";
import { Layout } from "containers/layout";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import it from "date-fns/locale/it";
import en from "date-fns/locale/en-GB";
import { useTranslation } from "react-i18next";
import { Locale } from "date-fns";
import { LoginPage } from "pages/login";
import { AxiosError, AxiosResponse } from "axios";
import { productAsync } from "core/store/modules/product";
import { CookieBanner } from "components/atoms/cookie-banner";

type languageOptionsType = {
  [key: string]: {
    locale: Locale;
  };
};

interface OwnProps {}
interface StateProps {}
interface DispatchProps {
  getUser: () => void;
  logout: () => void;
  getProductList: () => void;
  getPlanList: () => void;
}
export type Props = OwnProps & StateProps & DispatchProps;

const App: FunctionComponent<Props> = ({
  getUser,
  logout,
  getProductList,
  getPlanList,
}) => {
  const { keycloak } = useKeycloak();
  const { i18n } = useTranslation();
  const { theme } = useTheme();
  const content = useRoutes(routes);
  const languageOptions: languageOptionsType = {
    it: { locale: it },
    en: { locale: en },
  };
  const selectedLanguage = languageOptions[i18n.language] ?? {
    it: { locale: it },
  };

  useEffect(() => {
    if (keycloak.authenticated) {
      codeHttpClient.interceptors.request.use(
        (config) => {
          config.headers.Authorization = `Bearer ${keycloak.token}`;
          return config;
        },
        (error) => {
          return Promise.reject(error);
        }
      );

      codeHttpClient.interceptors.response.use(
        (response: AxiosResponse) => response,
        async (error: AxiosError) => {
          console.error(
            "ERROR: " + error.response?.status + " on " + error.config?.url
          );
          if (error.response && error.response.status === 401) {
            console.log("Auth FAILED");
            handleLogout();
          }
          if (
            error.response &&
            error.response.status === 402 &&
            (error.response.data as any)?.code === "account_suspended"
          ) {
            // Il backend ha bloccato un'operazione perche' l'account e' sospeso per
            // mancato pagamento: ricarico /account cosi' billingStatus si aggiorna e
            // compare il banner bloccante.
            getUser();
          }
          // Propagate the error if not a 401
          throw error;
        }
      );
    }
  }, [keycloak.authenticated, keycloak.token]);

  useEffect(() => {
    if (keycloak.authenticated) {
      // console.log(keycloak.token)
      getUser();
      getProductList();
      getPlanList();
    }
  }, [keycloak.authenticated, getUser]);

  const handleLogout = () => {
    logout();
    keycloak.logout();
  };

  if (keycloak.authenticated) {
    return (
      <MuiThemeProvider theme={createTheme(theme)}>
        {/* @ts-ignore */}
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={selectedLanguage.locale}
        >
          <Layout handleLogout={handleLogout}>{content}</Layout>
          <CookieBanner />
        </LocalizationProvider>
      </MuiThemeProvider>
    );
  } else {
    return (
      <MuiThemeProvider theme={createTheme(theme)}>
        <LoginPage />
      </MuiThemeProvider>
    );
  }
};

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  (state: RootState) => ({}),
  (dispatch: Dispatch) => ({
    getUser: () => dispatch(authAsync.getUser()),
    logout: () => dispatch(authAsync.logout()),
    getProductList: () => dispatch(productAsync.getProductList()),
    getPlanList: () => dispatch(productAsync.getPlanList()),
  })
)(App);
